import * as React from "react";
import { useIntl } from "@grandvisionhq/state";
import { formatPrice } from "@grandvisionhq/utils";
import { usePaymentsUIContext } from "../../../../../../context/PaymentsUI/context/PaymentsUI/context.js";
import { useGetCartPaymentMethods } from "../../../../../../hooks/hooks/use-get-cart-payment-methods.js";
import { usePaymentMethodLabelsMap } from "../../../../../../hooks/hooks/use-payment-method-labels-map.js";
import { usePaymentMethodForm } from "./use-payment-method-form.js";
import { PaymentForm } from "../../../../../../component/PaymentForm/component/PaymentForm/PaymentForm.js";
import { PaymentMethod } from "../../../../../../models/cart-payment-methods/models/cart-payment-methods/cart-payment-methods.js";
const useGetPaymentMethodList = (cartId, onPaymentCompleteHandler, selectedDonation, onDonationSelected, totalPrice, googlePayActions) => {
  const { titleLabels } = usePaymentMethodLabelsMap();
  const [getPaymentMethods, queryResult] = useGetCartPaymentMethods();
  const { isProcessingOrder } = usePaymentsUIContext();
  const { locale, currency } = useIntl();
  const loadPaymentMethods = React.useCallback(
    (cartId2) => {
      getPaymentMethods(cartId2);
    },
    [getPaymentMethods]
  );
  React.useEffect(() => {
    if (cartId) {
      loadPaymentMethods(cartId);
    }
  }, [cartId, getPaymentMethods, loadPaymentMethods]);
  const [renderPaymentForm, selectedPaymentDetails, resetPaymentDetails] = usePaymentMethodForm(
    googlePayActions?.handleGpayCtaClick,
    googlePayActions?.onAuthorize
  );
  const paymentMethods = React.useMemo(() => queryResult?.data, [queryResult?.data]);
  const error = React.useMemo(() => queryResult?.error, [queryResult?.error]);
  const loading = React.useMemo(() => queryResult?.loading, [queryResult?.loading]);
  const called = React.useMemo(() => queryResult?.called, [queryResult?.called]);
  const selectedPaymentData = React.useMemo(() => selectedPaymentDetails, [selectedPaymentDetails]);
  const onHandlePaymentAction = React.useCallback(() => {
    onPaymentCompleteHandler(selectedPaymentDetails);
  }, [onPaymentCompleteHandler, selectedPaymentDetails]);
  const paymentMethodItems = React.useMemo(() => {
    if (!paymentMethods || error) {
      return [];
    }
    const items = paymentMethods.map(({ provider, name, icons, paymentMethodPrice, ...rest }) => {
      const item = React.Children.toArray(icons) ?? [];
      let formConfig;
      const paymentMethodList = rest?.items;
      const cardBrands = rest?.brands;
      if (paymentMethodList) {
        formConfig = {
          items: paymentMethodList
        };
      }
      if (cardBrands) {
        formConfig = {
          brands: cardBrands
        };
      }
      if (name == PaymentMethod.GOOGLE_PAY && googlePayActions?.handleGpayCtaClick && googlePayActions?.onAuthorize) {
        formConfig = {
          onClick: googlePayActions?.handleGpayCtaClick,
          onAuthorized: googlePayActions?.onAuthorize
        };
      }
      const formComponent = renderPaymentForm({
        type: name,
        provider,
        config: formConfig
      });
      return {
        value: name,
        label: titleLabels[name],
        subtext: paymentMethodPrice?.additionalCosts ? `+ ${formatPrice({
          locale,
          value: paymentMethodPrice.additionalCosts,
          currency,
          isCentAmount: true,
          fixedDecimals: true
        })}` : void 0,
        id: `${provider}-${name}`,
        disabled: isProcessingOrder,
        aside: item,
        content: /* @__PURE__ */ React.createElement(
          PaymentForm,
          {
            paymentMethod: name,
            paymentProvider: provider,
            form: formComponent,
            onHandlePaymentAction,
            isActionDisabled: false,
            isActionLoading: false,
            selectedDonation,
            onDonationSelected,
            totalPrice: paymentMethodPrice?.emulatedTotalCartPrice ?? totalPrice,
            ..."donations" in rest && { donations: rest.donations }
          }
        )
      };
    });
    return items;
  }, [
    currency,
    error,
    googlePayActions?.handleGpayCtaClick,
    googlePayActions?.onAuthorize,
    isProcessingOrder,
    locale,
    onDonationSelected,
    onHandlePaymentAction,
    paymentMethods,
    renderPaymentForm,
    selectedDonation,
    titleLabels,
    totalPrice
  ]);
  return [
    React.useMemo(() => paymentMethodItems, [paymentMethodItems]),
    selectedPaymentData,
    resetPaymentDetails,
    { error, called, loading, data: paymentMethods || [] }
  ];
};
export {
  useGetPaymentMethodList
};
//# sourceMappingURL=use-get-payment-method-list.js.map
